import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Loader2, Calculator, Factory } from 'lucide-react';
import { updateTask } from '@/utils/api';
import { toast } from 'sonner';

const LossRow = ({ label, pct, wt, baseWeight, onPctChange, onWtChange, testId }) => {
  return (
    <div className="grid grid-cols-[1fr_100px_100px] gap-2 items-center">
      <span className="text-xs text-foreground font-medium">{label}</span>
      <div className="relative">
        <Input
          type="number"
          step="0.01"
          value={pct ?? ''}
          onChange={e => onPctChange(parseFloat(e.target.value) || 0)}
          className="h-7 text-xs pr-6"
          placeholder="0"
          data-testid={`${testId}-pct`}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">%</span>
      </div>
      <div className="relative">
        <Input
          type="number"
          step="0.001"
          value={wt ?? ''}
          onChange={e => onWtChange(parseFloat(e.target.value) || 0)}
          className="h-7 text-xs pr-6"
          placeholder="0"
          data-testid={`${testId}-wt`}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">g</span>
      </div>
    </div>
  );
};

const ManufacturingTab = ({ task, onUpdate }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (task) {
      const baseWt = task.mfg_expected_net_weight ?? task.net_weight ?? 0;
      const stages = ['casting', 'filing', 'setting', 'polish', 'qc'];
      const initial = {
        mfg_expected_net_weight: task.mfg_expected_net_weight ?? task.net_weight ?? null,
        mfg_sprue_weight: task.mfg_sprue_weight ?? null,
        mfg_casting_loss_pct: task.mfg_casting_loss_pct ?? 2,
        mfg_casting_loss_wt: task.mfg_casting_loss_wt ?? null,
        mfg_filing_loss_pct: task.mfg_filing_loss_pct ?? 1,
        mfg_filing_loss_wt: task.mfg_filing_loss_wt ?? null,
        mfg_setting_loss_pct: task.mfg_setting_loss_pct ?? 0.5,
        mfg_setting_loss_wt: task.mfg_setting_loss_wt ?? null,
        mfg_polish_loss_pct: task.mfg_polish_loss_pct ?? 0.5,
        mfg_polish_loss_wt: task.mfg_polish_loss_wt ?? null,
        mfg_qc_loss_pct: task.mfg_qc_loss_pct ?? 0.2,
        mfg_qc_loss_wt: task.mfg_qc_loss_wt ?? null,
        mfg_total_loss_pct: task.mfg_total_loss_pct ?? null,
        mfg_total_loss_wt: task.mfg_total_loss_wt ?? null,
        mfg_casting_weight: task.mfg_casting_weight ?? null,
        mfg_wax_weight: task.mfg_wax_weight ?? null,
        mfg_wax_conversion_factor: task.mfg_wax_conversion_factor ?? 0.55,
        mfg_notes: task.mfg_notes ?? '',
      };
      
      // Recalculate weights from percentages if not already set
      let totalWt = 0;
      let totalPct = 0;
      stages.forEach(s => {
        const pct = initial[`mfg_${s}_loss_pct`] || 0;
        if (!initial[`mfg_${s}_loss_wt`] && baseWt > 0) {
          initial[`mfg_${s}_loss_wt`] = Math.round((pct / 100) * baseWt * 1000) / 1000;
        }
        totalPct += initial[`mfg_${s}_loss_pct`] || 0;
        totalWt += initial[`mfg_${s}_loss_wt`] || 0;
      });
      initial.mfg_total_loss_pct = Math.round(totalPct * 100) / 100;
      initial.mfg_total_loss_wt = Math.round(totalWt * 1000) / 1000;
      
      const sprue = initial.mfg_sprue_weight || 0;
      const netWt = initial.mfg_expected_net_weight || 0;
      initial.mfg_casting_weight = Math.round((netWt + totalWt + sprue) * 1000) / 1000;
      
      const convFactor = initial.mfg_wax_conversion_factor || 0.55;
      initial.mfg_wax_weight = Math.round(initial.mfg_casting_weight * convFactor * 1000) / 1000;
      
      setData(initial);
      setDirty(false);
    }
  }, [task?.id]);

  const updateField = useCallback((field, value) => {
    setData(prev => {
      const next = { ...prev, [field]: value };
      // Recalculate based on which field changed
      const baseWt = next.mfg_expected_net_weight || 0;

      const stages = ['casting', 'filing', 'setting', 'polish', 'qc'];

      if (field.endsWith('_pct')) {
        // User changed a percentage -> calculate the weight
        const stage = field.replace('mfg_', '').replace('_loss_pct', '');
        next[`mfg_${stage}_loss_wt`] = baseWt > 0 ? Math.round((value / 100) * baseWt * 1000) / 1000 : 0;
      } else if (field.endsWith('_wt') && field.includes('_loss_')) {
        // User changed a weight -> calculate the percentage
        const stage = field.replace('mfg_', '').replace('_loss_wt', '');
        next[`mfg_${stage}_loss_pct`] = baseWt > 0 ? Math.round((value / baseWt) * 100 * 100) / 100 : 0;
      } else if (field === 'mfg_expected_net_weight') {
        // Recalculate all weights from percentages
        stages.forEach(s => {
          const pct = next[`mfg_${s}_loss_pct`] || 0;
          next[`mfg_${s}_loss_wt`] = value > 0 ? Math.round((pct / 100) * value * 1000) / 1000 : 0;
        });
      }

      // Calculate totals
      let totalPct = 0;
      let totalWt = 0;
      stages.forEach(s => {
        totalPct += next[`mfg_${s}_loss_pct`] || 0;
        totalWt += next[`mfg_${s}_loss_wt`] || 0;
      });
      next.mfg_total_loss_pct = Math.round(totalPct * 100) / 100;
      next.mfg_total_loss_wt = Math.round(totalWt * 1000) / 1000;

      // Casting Weight = Expected Net Weight + Total Loss + Sprue Weight
      const sprue = next.mfg_sprue_weight || 0;
      const netWt = next.mfg_expected_net_weight || 0;
      next.mfg_casting_weight = Math.round((netWt + totalWt + sprue) * 1000) / 1000;

      // Wax Weight = Casting Weight * Conversion Factor
      const convFactor = next.mfg_wax_conversion_factor || 0.55;
      next.mfg_wax_weight = Math.round(next.mfg_casting_weight * convFactor * 1000) / 1000;

      return next;
    });
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!task?.id) return;
    setSaving(true);
    try {
      await updateTask(task.id, data);
      toast.success('Manufacturing details saved');
      setDirty(false);
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <div className="p-4 space-y-4" data-testid="manufacturing-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Factory size={16} className="text-primary" />
          <h3 className="text-sm font-bold text-foreground">Manufacturing Details</h3>
          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{task.style_no || task.internal_cad_no}</span>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving || !dirty} className="h-7 text-xs" data-testid="mfg-save-btn">
          {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Save size={12} className="mr-1" />}
          {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Core Inputs */}
        <div className="space-y-3">
          <div className="border border-border rounded-lg p-3 bg-card">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Core Weight Inputs</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Expected Net Weight (g)</Label>
                <Input
                  type="number" step="0.01"
                  value={data.mfg_expected_net_weight ?? ''}
                  onChange={e => updateField('mfg_expected_net_weight', parseFloat(e.target.value) || 0)}
                  className="h-7 text-xs mt-0.5"
                  data-testid="mfg-expected-net-wt"
                />
              </div>
              <div>
                <Label className="text-[11px]">Sprue Weight (g)</Label>
                <Input
                  type="number" step="0.01"
                  value={data.mfg_sprue_weight ?? ''}
                  onChange={e => updateField('mfg_sprue_weight', parseFloat(e.target.value) || 0)}
                  className="h-7 text-xs mt-0.5"
                  data-testid="mfg-sprue-wt"
                />
              </div>
            </div>
          </div>

          {/* Stage-wise Losses */}
          <div className="border border-border rounded-lg p-3 bg-card">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Stage-wise Gold Losses</h4>
            <div className="grid grid-cols-[1fr_100px_100px] gap-2 items-center mb-1.5">
              <span className="text-[10px] text-muted-foreground">Stage</span>
              <span className="text-[10px] text-muted-foreground text-center">Percentage</span>
              <span className="text-[10px] text-muted-foreground text-center">Weight (g)</span>
            </div>
            <div className="space-y-1.5">
              <LossRow label="Casting Loss" pct={data.mfg_casting_loss_pct} wt={data.mfg_casting_loss_wt} baseWeight={data.mfg_expected_net_weight} onPctChange={v => updateField('mfg_casting_loss_pct', v)} onWtChange={v => updateField('mfg_casting_loss_wt', v)} testId="mfg-casting" />
              <LossRow label="Filing Loss" pct={data.mfg_filing_loss_pct} wt={data.mfg_filing_loss_wt} baseWeight={data.mfg_expected_net_weight} onPctChange={v => updateField('mfg_filing_loss_pct', v)} onWtChange={v => updateField('mfg_filing_loss_wt', v)} testId="mfg-filing" />
              <LossRow label="Setting Loss" pct={data.mfg_setting_loss_pct} wt={data.mfg_setting_loss_wt} baseWeight={data.mfg_expected_net_weight} onPctChange={v => updateField('mfg_setting_loss_pct', v)} onWtChange={v => updateField('mfg_setting_loss_wt', v)} testId="mfg-setting" />
              <LossRow label="Polish Loss" pct={data.mfg_polish_loss_pct} wt={data.mfg_polish_loss_wt} baseWeight={data.mfg_expected_net_weight} onPctChange={v => updateField('mfg_polish_loss_pct', v)} onWtChange={v => updateField('mfg_polish_loss_wt', v)} testId="mfg-polish" />
              <LossRow label="QC Loss" pct={data.mfg_qc_loss_pct} wt={data.mfg_qc_loss_wt} baseWeight={data.mfg_expected_net_weight} onPctChange={v => updateField('mfg_qc_loss_pct', v)} onWtChange={v => updateField('mfg_qc_loss_wt', v)} testId="mfg-qc" />
              {/* Total */}
              <div className="grid grid-cols-[1fr_100px_100px] gap-2 items-center pt-1.5 border-t border-border">
                <span className="text-xs font-bold text-foreground">Total Loss</span>
                <div className="text-xs font-bold text-center text-amber-600" data-testid="mfg-total-loss-pct">{data.mfg_total_loss_pct ?? 0}%</div>
                <div className="text-xs font-bold text-center text-amber-600" data-testid="mfg-total-loss-wt">{data.mfg_total_loss_wt ?? 0}g</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Calculated Results */}
        <div className="space-y-3">
          <div className="border-2 border-primary/30 rounded-lg p-3 bg-primary/5">
            <div className="flex items-center gap-1.5 mb-3">
              <Calculator size={14} className="text-primary" />
              <h4 className="text-xs font-semibold text-primary uppercase tracking-wider">Auto-Calculated Results</h4>
            </div>
            <div className="space-y-3">
              {/* Casting Weight */}
              <div className="bg-card rounded-md p-2.5 border border-border">
                <div className="text-[10px] text-muted-foreground mb-0.5">Casting Weight (auto)</div>
                <div className="text-lg font-bold text-foreground" data-testid="mfg-casting-weight">{data.mfg_casting_weight ?? 0}g</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">= Net Wt ({data.mfg_expected_net_weight || 0}g) + Losses ({data.mfg_total_loss_wt || 0}g) + Sprue ({data.mfg_sprue_weight || 0}g)</div>
              </div>
              {/* Wax Weight */}
              <div className="bg-card rounded-md p-2.5 border border-border">
                <div className="text-[10px] text-muted-foreground mb-0.5">Wax Weight (derived)</div>
                <div className="text-lg font-bold text-foreground" data-testid="mfg-wax-weight">{data.mfg_wax_weight ?? 0}g</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-muted-foreground">Conversion Factor:</span>
                  <Input
                    type="number" step="0.01"
                    value={data.mfg_wax_conversion_factor ?? 0.55}
                    onChange={e => updateField('mfg_wax_conversion_factor', parseFloat(e.target.value) || 0.55)}
                    className="h-6 text-xs w-16"
                    data-testid="mfg-wax-factor"
                  />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">= Casting Wt ({data.mfg_casting_weight || 0}g) x {data.mfg_wax_conversion_factor || 0.55}</div>
              </div>
            </div>
          </div>

          {/* Summary Card */}
          <div className="border border-border rounded-lg p-3 bg-card">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Weight Summary</h4>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Design Net Weight:</span><span className="font-medium">{task.net_weight || '\u2014'}g</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Expected Net Weight:</span><span className="font-medium">{data.mfg_expected_net_weight || '\u2014'}g</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Gold Loss:</span><span className="font-medium text-amber-600">{data.mfg_total_loss_wt || 0}g ({data.mfg_total_loss_pct || 0}%)</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sprue Weight:</span><span className="font-medium">{data.mfg_sprue_weight || 0}g</span></div>
              <div className="flex justify-between border-t border-border pt-1.5"><span className="font-semibold">Casting Weight:</span><span className="font-bold text-primary">{data.mfg_casting_weight || 0}g</span></div>
              <div className="flex justify-between"><span className="font-semibold">Wax Weight:</span><span className="font-bold text-primary">{data.mfg_wax_weight || 0}g</span></div>
            </div>
          </div>

          {/* Notes */}
          <div className="border border-border rounded-lg p-3 bg-card">
            <Label className="text-[11px] font-semibold">Manufacturing Notes</Label>
            <Textarea
              value={data.mfg_notes || ''}
              onChange={e => { setData(prev => ({ ...prev, mfg_notes: e.target.value })); setDirty(true); }}
              placeholder="Additional manufacturing notes..."
              rows={3}
              className="text-xs mt-1"
              data-testid="mfg-notes"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManufacturingTab;
